import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Form } from 'antd';

import { ContactType, IContact } from '../../../models';
import ContactsService, { GetAll } from '../../../services/contact.service';
import { Api } from '../../../services/api';

export interface ContactDefaultFilters {
  type?: ContactType;
  search?: string;
}

export const defaultContactsFilters: ContactDefaultFilters = {
  type: ContactType.All,
  search: '',
};

interface FetchContactsProps {
  defaultFilters?: ContactDefaultFilters;
  pageNumber?: number;
  pageSize?: number;
  text?: string;
  type?: ContactType;
}

export function useContacts(props: FetchContactsProps = {}) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IContact[]>();
  const [total, setTotal] = useState<number>();
  const [pagination, setPagination] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<ContactDefaultFilters>({
    type: props.defaultFilters?.type ?? defaultContactsFilters.type,
    search: props.defaultFilters?.search ?? defaultContactsFilters.search,
  });

  const load = async () => {
    const filters = {
      pageNumber: props.pageNumber ?? pagination.pageNumber,
      pageSize: props.pageSize ?? pagination.pageSize,
      text: props.text ?? filter.search,
      type: props.type ?? filter.type,
    };

    const diffType = !_.isEqual(defaultContactsFilters.type, filters.type);
    const diffSearch = !_.isEqual(defaultContactsFilters.search, filters.text);

    const counter = [diffType, diffSearch].filter(Boolean).length;

    setFilterCounter(counter);

    // Filter all should not be passed
    if (filters.type === ContactType.All) {
      delete filters.type;
    }

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters, { cancelToken: cancelToken.token }),
      (rs) => {
        setLoading(false);
        setData(
          rs.data.map((item) => ({
            ...item,
            ...{ dob: item.dob ? moment(item.dob) : undefined },
          })),
        );
        setTotal(rs.meta.total);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.text, props.type, filter, pagination]);

  return { loading, error, data, total, pagination, setPagination, filterCounter, setFilter, reloadList: load };
}

export function useRemoveContact() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [obj, setObj] = useState<IContact|undefined>();

  const remove = async (id: string) => {
    setIsDeleting(true);
    try {
      await ContactsService.Delete(id);
    } finally {
      setIsDeleting(false);
    }
  };

  const execute = async () => {
    if (!obj?.id) {
      throw new Error(`should first select a contact to delete`);
    }
    setIsDeleting(true);
    try {
      await ContactsService.Delete(obj.id);
    } finally {
      setIsDeleting(false);
    }
  }

  const select = (obj: IContact) => {
    setObj(obj);
  }

  const reset = () => {
    setObj(undefined);
  }

  return { isDeleting, remove, execute, select, reset, obj };
}

export function useSaveContact() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const save = async (payload: IContact) => {
    setIsSaving(true);
    try {
      if (payload.id) {
        return await ContactsService.Update(payload.id, payload);
      }
      return await ContactsService.Create(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, save, form };
}
